import React, { useState, useEffect, useRef } from 'react';
import api from '../../../api/axios';
import toast from 'react-hot-toast';
import Select from 'react-select';
import { Box, Paper, Typography, Button, Chip, TextField, MenuItem, Select as MuiSelect, FormControl, InputLabel } from '@mui/material';

const ZScore = () => {
  const [step, setStep] = useState(0);
  const [datasets, setDatasets] = useState([]);
  const [selectedDatasetId, setSelectedDatasetId] = useState('');
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [results, setResults] = useState(null);
  const [isLoadingAnalysis, setIsLoadingAnalysis] = useState(false);
  
  // Analysis state variables
  const [analysisStatus, setAnalysisStatus] = useState('draft');
  const [analysisDescription, setAnalysisDescription] = useState('');
  const [savedAnalysisId, setSavedAnalysisId] = useState(null);
  const [updatingMetadata, setUpdatingMetadata] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [savingComment, setSavingComment] = useState(false);
  const [isCommentsExpanded, setIsCommentsExpanded] = useState(true);
  
  // Mode detection
  const mode = new URLSearchParams(window.location.search).get('mode');
  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit' || !mode;
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showScatterEditor, setShowScatterEditor] = useState(false);
  const [showBoxplotEditor, setShowBoxplotEditor] = useState(false);
  const [showHistEditor, setShowHistEditor] = useState(false);
  const scatterRef = useRef(null);
  const boxplotRef = useRef(null);
  const histRef = useRef(null);
  const [saveColName, setSaveColName] = useState('');
  
  const [scatterTitle, setScatterTitle] = useState('Z-Score Plot');
  const [boxplotTitle, setBoxplotTitle] = useState('Boxplot');
  const [boxplotYLabel, setBoxplotYLabel] = useState('');
  const [histTitle, setHistTitle] = useState('Histogram with Target Mean');
  const [histXLabel, setHistXLabel] = useState('');
  const [histY2Label, setHistY2Label] = useState('Mean Target');
  
  const [formData, setFormData] = useState({
    Default_Date: null, Risk_driver: null, Target_variable: null, n_bins: 10, threshold: 3.0
  });

  const columnOptions = columns.map(c => ({ value: c, label: c }));

  // Load saved analysis on mount
  useEffect(() => {
    const analysisId = new URLSearchParams(window.location.search).get('analysis_id');
    if (analysisId && !isNaN(parseInt(analysisId))) {
      loadSavedAnalysis(parseInt(analysisId));
    } else {
      fetchDatasets();
    }
  }, []);

  useEffect(() => {
    if (!window.Plotly) {
      const script = document.createElement('script');
      script.src = 'https://cdn.plot.ly/plotly-2.27.0.min.js';
      document.head.appendChild(script);
    }
  }, []);

  const loadSavedAnalysis = async (analysisId) => {
    setIsLoadingAnalysis(true);
    try {
      const res = await api.get(`/analyses/${analysisId}`);
      const analysis = res.data;
      
      if (analysis.status) setAnalysisStatus(analysis.status);
      if (analysis.description) setAnalysisDescription(analysis.description);
      setSavedAnalysisId(analysis.id);
      if (analysis.comments && Array.isArray(analysis.comments)) setComments(analysis.comments);
      
      if (analysis.input_parameters) {
        // Restore dataset
        setSelectedDatasetId(analysis.input_parameters.dataset_id);
        setSelectedDataset({
          id: analysis.input_parameters.dataset_id,
          name: analysis.input_parameters.dataset_name,
          rows: analysis.input_parameters.rows,
          columns: analysis.input_parameters.columns
        });
        
        // Restore columns
        if (analysis.input_parameters.columns) {
          setColumns(analysis.input_parameters.columns);
        }
        
        // Restore form data
        setFormData({
          Default_Date: analysis.input_parameters.Default_Date ? { value: analysis.input_parameters.Default_Date, label: analysis.input_parameters.Default_Date } : null,
          Risk_driver: analysis.input_parameters.Risk_driver ? { value: analysis.input_parameters.Risk_driver, label: analysis.input_parameters.Risk_driver } : null,
          Target_variable: analysis.input_parameters.Target_variable ? { value: analysis.input_parameters.Target_variable, label: analysis.input_parameters.Target_variable } : null,
          n_bins: analysis.input_parameters.n_bins || 10,
          threshold: analysis.input_parameters.threshold || 3.0
        });
        
        // Restore plot titles
        if (analysis.input_parameters.scatterTitle) setScatterTitle(analysis.input_parameters.scatterTitle);
        if (analysis.input_parameters.boxplotTitle) setBoxplotTitle(analysis.input_parameters.boxplotTitle);
        if (analysis.input_parameters.boxplotYLabel) setBoxplotYLabel(analysis.input_parameters.boxplotYLabel);
        if (analysis.input_parameters.histTitle) setHistTitle(analysis.input_parameters.histTitle);
        if (analysis.input_parameters.histXLabel) setHistXLabel(analysis.input_parameters.histXLabel);
        if (analysis.input_parameters.histY2Label) setHistY2Label(analysis.input_parameters.histY2Label);
        if (analysis.input_parameters.saveColName) setSaveColName(analysis.input_parameters.saveColName);
      }
      
      if (analysis.output_result) {
        setResults(analysis.output_result);
        setStep(2);
        toast.success('Analysis loaded with results');
      } else if (analysis.input_parameters) {
        setStep(1);
        toast.success('Analysis loaded. Please run the analysis to see results.');
      }
    } catch (err) {
      toast.error('Failed to load analysis');
      fetchDatasets();
    } finally {
      setIsLoadingAnalysis(false);
    }
  };

  const fetchDatasets = async () => {
    try {
      const projectId = new URLSearchParams(window.location.search).get('project_id');
      if (!projectId) {
        setDatasets([]);
        return;
      }
      const res = await api.get(`/api/v1/tables/project/${projectId}/datasets`);
      const datasets = res.data.map(ds => ({
        id: ds.id,
        name: ds.original_filename,
        size_mb: (ds.file_size / 1e6).toFixed(2),
        rows: ds.row_count,
        columns: ds.column_count,
        exists: true
      }));
      setDatasets(datasets);
    } catch (err) {
      toast.error('Failed to load datasets');
    }
  };

  const handleSelectDataset = async () => {
    if (!selectedDatasetId) return;
    setLoading(true);
    try {
      const res = await api.post('/api/zscore/select-dataset', { dataset_id: parseInt(selectedDatasetId) });
      const selectedDs = datasets.find(ds => ds.id == selectedDatasetId);
      setSelectedDataset({
        id: res.data.table_id,
        name: selectedDs?.name || 'Unknown',
        rows: res.data.rows,
        columns: res.data.columns
      });
      setColumns(res.data.columns || []);
      setResults(null);
      setStep(1);
      toast.success(`Loaded: ${res.data.rows?.toLocaleString()} rows`);
    } catch (err) {
      toast.error('Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const renderScatter = (custom = null) => {
    if (!results?.scatter_data || !scatterRef.current || !window.Plotly) return;
    const data = results.scatter_data;
    const colors = data.is_outlier.map(o => o ? '#e74c3c' : '#3498db');
    const displayLimit = Math.min(data.x.length, 5000);
    window.Plotly.newPlot(scatterRef.current, [{
      x: data.x.slice(0, displayLimit), 
      y: data.z_scores.slice(0, displayLimit), 
      type: 'scatter', mode: 'markers',
      marker: { color: colors.slice(0, displayLimit), size: 6 }, 
      name: 'Z-Score'
    }], {
      title: custom?.title || scatterTitle, 
      xaxis: { title: 'Index' }, 
      yaxis: { title: 'Z-Score' },
      shapes: [
        { type: 'line', x0: 0, x1: 1, xref: 'paper', y0: data.threshold, y1: data.threshold, line: { color: '#e74c3c', dash: 'dash' } },
        { type: 'line', x0: 0, x1: 1, xref: 'paper', y0: -data.threshold, y1: -data.threshold, line: { color: '#e74c3c', dash: 'dash' } }
      ],
      template: 'plotly_white', height: 400
    });
  };

  const renderBoxplot = (custom = null) => {
    if (!results?.boxplot_data || !boxplotRef.current || !window.Plotly) return;
    const data = results.boxplot_data;
    window.Plotly.newPlot(boxplotRef.current, [
      { y: data.inliers, type: 'box', name: 'Inliers', marker: { color: '#3498db' } },
      { y: data.outliers, type: 'box', name: 'Outliers', marker: { color: '#e74c3c' } }
    ], {
      title: custom?.title || boxplotTitle,
      yaxis: { title: custom?.ylabel || boxplotYLabel || data.risk_driver },
      template: 'plotly_white', height: 400,
      legend: { orientation: 'h', y: -0.2 }
    });
  };

  const renderHist = (custom = null) => {
    if (!results?.hist_target_data || !histRef.current || !window.Plotly) return;
    const data = results.hist_target_data;
    window.Plotly.newPlot(histRef.current, [
      { x: data.x, type: 'histogram', nbinsx: data.bins, name: 'Frequency', marker: { color: '#3498db', opacity: 0.7 } },
      { x: data.bin_midpoints, y: data.bin_means, type: 'scatter', mode: 'lines+markers', name: custom?.y2label || histY2Label, line: { color: '#e74c3c', width: 3 }, yaxis: 'y2' }
    ], {
      title: custom?.title || histTitle,
      xaxis: { title: custom?.xlabel || histXLabel || data.risk_driver },
      yaxis: { title: 'Frequency' },
      yaxis2: { title: custom?.y2label || histY2Label, overlaying: 'y', side: 'right' },
      template: 'plotly_white', height: 400,
      legend: { orientation: 'h', y: -0.2 }
    });
  };

  useEffect(() => {
    if (results) {
      setTimeout(() => {
        renderScatter();
        renderBoxplot();
        renderHist();
      }, 100);
    }
  }, [results]);

  const handleSaveParameters = async () => {
    if (!formData.Risk_driver) {
      toast.error('Risk Driver is required');
      return;
    }
    setAnalyzing(true);
    try {
      await api.post('/api/zscore/step0', {
        Risk_driver: formData.Risk_driver.value,
        Target_variable: formData.Target_variable?.value || '',
        Default_Date: formData.Default_Date?.value || '',
        n_bins: formData.n_bins,
        threshold: formData.threshold
      });
      toast.success('Parameters saved');
      setStep(2);
      await handleRunAnalysis();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleRunAnalysis = async () => {
    setAnalyzing(true);
    try {
      const res = await api.post('/api/zscore/step1', {
        Risk_driver: formData.Risk_driver.value,
        Target_variable: formData.Target_variable?.value || '',
        Default_Date: formData.Default_Date?.value || '',
        n_bins: formData.n_bins,
        threshold: formData.threshold
      });
      setResults(res.data.results);
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSaveColumn = async () => {
    if (!saveColName.trim()) {
      toast.error('Enter a column name');
      return;
    }
    setLoading(true);
    try {
      await api.post('/api/zscore/step2', { new_column_name: saveColName.trim() });
      toast.success(`Saved as '${saveColName}'`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/api/zscore/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'zscore.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export started');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const handleSaveAnalysis = async () => {
    if (!selectedDataset) {
      toast.error('No dataset selected');
      return;
    }
    const projectId = new URLSearchParams(window.location.search).get('project_id');
    if (!projectId) {
      toast.error('No project selected');
      return;
    }
    
    if (!results) {
      toast.error('No results to save. Please run the analysis first.');
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        project_id: parseInt(projectId),
        module_name: 'zscore',
        description: analysisDescription,
        input_parameters: {
          dataset_id: selectedDataset.id,
          dataset_name: selectedDataset.name,
          rows: selectedDataset.rows,
          columns: selectedDataset.columns,
          Risk_driver: formData.Risk_driver?.value || '',
          Target_variable: formData.Target_variable?.value || '',
          Default_Date: formData.Default_Date?.value || '',
          n_bins: formData.n_bins,
          threshold: formData.threshold,
          scatterTitle: scatterTitle,
          boxplotTitle: boxplotTitle,
          boxplotYLabel: boxplotYLabel,
          histTitle: histTitle,
          histXLabel: histXLabel,
          histY2Label: histY2Label,
          saveColName: saveColName
        },
        output_result: results,
        status: analysisStatus,
        comments: comments
      };
      const res = await api.post('/analyses', payload);
      toast.success(`Analysis saved with ID: ${res.data.id}`);
      setSavedAnalysisId(res.data.id);
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Failed to save analysis');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateMetadata = async () => {
    if (!savedAnalysisId) {
      toast.error('No analysis saved yet');
      return;
    }
    setUpdatingMetadata(true);
    try {
      await api.patch(`/analyses/${savedAnalysisId}`, {
        status: analysisStatus,
        description: analysisDescription
      });
      toast.success('Status and description updated');
    } catch (err) {
      toast.error('Failed to update');
    } finally {
      setUpdatingMetadata(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    const commentObj = {
      id: Date.now(),
      text: newComment,
      created_at: new Date().toISOString(),
      user_id: localStorage.getItem('user_id') || 'user',
      user_email: localStorage.getItem('user_email') || 'Unknown'
    };
    const updatedComments = [...comments, commentObj];
    
    if (savedAnalysisId) {
      setSavingComment(true);
      try {
        await api.patch(`/analyses/${savedAnalysisId}`, { comments: updatedComments });
        setComments(updatedComments);
        setNewComment('');
        toast.success('Comment added');
      } catch (err) {
        toast.error('Failed to add comment');
      } finally {
        setSavingComment(false);
      }
    } else {
      setComments(updatedComments);
      setNewComment('');
      toast.success('Comment added (will be saved when you save the analysis)');
    }
  };

  const cs = { 
    control: (b) => ({ ...b, minHeight: '36px', fontSize: '13px', borderColor: '#e0e0e0' }), 
    valueContainer: (b) => ({ ...b, padding: '0 8px' }), 
    placeholder: (b) => ({ ...b, fontSize: '13px', color: '#95a5a6' }), 
    menu: (b) => ({ ...b, zIndex: 9999 }), 
    menuPortal: (b) => ({ ...b, zIndex: 9999 }) 
  };
  
  const stepTabs = ['Select', 'Review', 'Results'];
  const gs = { border: '1px solid #e0e0e0', borderRadius: '8px', marginBottom: '16px', overflow: 'hidden' };
  const ghs = { background: '#f5f7fa', padding: '12px 16px', fontWeight: 600, fontSize: '14px', color: '#2c3e50' };
  const gcs = { padding: '16px', background: 'white' };
  const inputStyle = { width: '100%', padding: '10px 12px', fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px' };

  if (isLoadingAnalysis) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div>Loading analysis...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '22px', marginBottom: '16px', color: '#2c3e50' }}>Z-Score Outlier Detection</h1>
      
      {isViewMode && results && (
        <div style={{ background: '#e8f8f5', padding: '8px 16px', borderRadius: '8px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '13px', color: '#1abc9c' }}>👁️ Viewing saved analysis (read-only)</span>
          <button
            onClick={() => {
              const url = new URL(window.location.href);
              url.searchParams.set('mode', 'edit');
              window.location.href = url.toString();
            }}
            style={{ padding: '4px 12px', fontSize: '12px', background: '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            Modify
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: '2px', marginBottom: '20px', borderBottom: '1px solid #e0e0e0', paddingBottom: '8px' }}>
        {stepTabs.map((tab, idx) => (
          <button
            key={idx}
            onClick={() => {
              if (idx === 0) setStep(0);
              else if (idx === 1 && selectedDataset) setStep(1);
              else if (idx === 2 && results) setStep(2);
            }}
            style={{
              padding: '6px 12px',
              fontSize: '11px',
              background: step === idx ? '#e74c3c' : 'transparent',
              color: step === idx ? 'white' : '#7f8c8d',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Step 0: Select Dataset */}
      {step === 0 && (
        <div style={gs}>
          <div style={ghs}>📊 Select Dataset</div>
          <div style={gcs}>
            {selectedDataset && (
              <div style={{ 
                marginBottom: '16px', 
                padding: '12px', 
                background: '#e8f8f5', 
                borderRadius: '8px',
                borderLeft: '4px solid #1abc9c'
              }}>
                <div style={{ fontSize: '13px', color: '#2c3e50', fontWeight: 500 }}>
                  📌 Currently selected dataset:
                </div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#1abc9c', marginTop: '4px' }}>
                  {selectedDataset.name}
                </div>
                <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '4px' }}>
                  {selectedDataset.rows?.toLocaleString()} rows • {selectedDataset.columns?.length || 0} columns
                </div>
              </div>
            )}
            
            <div style={{ display: 'flex', gap: '12px' }}>
              <select
                value={selectedDatasetId}
                onChange={(e) => {
                  setSelectedDatasetId(e.target.value);
                  setSelectedDataset(null);
                  setColumns([]);
                }}
                disabled={isViewMode}
                style={{ flex: 1, padding: '12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px' }}
              >
                <option value="">-- Select a dataset --</option>
                {datasets.map((ds) => (
                  <option key={ds.id} value={ds.id}>
                    {ds.name} ({ds.rows?.toLocaleString()} rows)
                  </option>
                ))}
              </select>
              <button
                onClick={handleSelectDataset}
                disabled={!selectedDatasetId || loading || isViewMode}
                style={{
                  padding: '12px 24px',
                  background: loading || isViewMode ? '#95a5a6' : '#1abc9c',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: loading || isViewMode ? 'not-allowed' : 'pointer',
                  fontSize: '13px'
                }}
              >
                {loading ? 'Loading...' : 'Load Dataset'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 1: Parameters */}
      {step === 1 && selectedDataset && (
        <div>
          <div style={gs}>
            <div style={ghs}>📋 Dataset Summary</div>
            <div style={gcs}>
              <div style={{ display: 'flex', gap: '12px' }}>
                <Chip label={`${selectedDataset.rows?.toLocaleString()} rows`} sx={{ background: '#ecf0f1' }} />
                <Chip label={`${selectedDataset.columns?.length || 0} columns`} sx={{ background: '#ecf0f1' }} />
              </div>
            </div>
          </div>

          <div style={gs}>
            <div style={ghs}>⚙️ Parameters</div>
            <div style={gcs}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Risk Driver *</label>
                  <Select 
                    options={columnOptions} 
                    value={formData.Risk_driver} 
                    onChange={v => setFormData({...formData, Risk_driver: v})} 
                    styles={{ control: (b) => ({ ...b, minHeight: '36px', fontSize: '13px' }) }}
                    menuPortalTarget={document.body} 
                    placeholder="Select risk driver..."
                    isDisabled={isViewMode}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Target Variable (optional)</label>
                  <Select 
                    options={columnOptions} 
                    value={formData.Target_variable} 
                    onChange={v => setFormData({...formData, Target_variable: v})} 
                    isClearable 
                    styles={{ control: (b) => ({ ...b, minHeight: '36px', fontSize: '13px' }) }}
                    menuPortalTarget={document.body} 
                    placeholder="Select target variable..."
                    isDisabled={isViewMode}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Default Date (optional)</label>
                  <Select 
                    options={columnOptions} 
                    value={formData.Default_Date} 
                    onChange={v => setFormData({...formData, Default_Date: v})} 
                    isClearable 
                    styles={{ control: (b) => ({ ...b, minHeight: '36px', fontSize: '13px' }) }}
                    menuPortalTarget={document.body} 
                    placeholder="Select date column..."
                    isDisabled={isViewMode}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <button 
                    type="button" 
                    onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                    disabled={isViewMode}
                    style={{ 
                      width: '100%', 
                      padding: '10px 12px', 
                      fontSize: '13px', 
                      background: showAdvancedOptions ? '#e74c3c' : '#f8f9fa', 
                      color: showAdvancedOptions ? 'white' : '#7f8c8d', 
                      border: '1px solid #ddd', 
                      borderRadius: '6px', 
                      cursor: isViewMode ? 'not-allowed' : 'pointer',
                      opacity: isViewMode ? 0.5 : 1
                    }}>
                    {showAdvancedOptions ? '✓ Advanced Options' : '⚙️ Advanced Options'}
                  </button>
                </div>
              </div>

              {showAdvancedOptions && (
                <div style={{ padding: '16px', background: '#f9f9f9', borderRadius: '6px', marginBottom: '16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Number of Bins</label>
                    <input 
                      type="number" 
                      value={formData.n_bins} 
                      onChange={(e) => setFormData({...formData, n_bins: parseInt(e.target.value)})} 
                      min="5" 
                      max="100" 
                      style={inputStyle}
                      disabled={isViewMode}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Z-Score Threshold</label>
                    <input 
                      type="number" 
                      step="0.1" 
                      value={formData.threshold} 
                      onChange={(e) => setFormData({...formData, threshold: parseFloat(e.target.value)})} 
                      min="1.0" 
                      max="5.0" 
                      style={inputStyle}
                      disabled={isViewMode}
                    />
                    <small style={{ color: '#7f8c8d', fontSize: 10 }}>Standard: 3.0</small>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setStep(0)}
                  disabled={isViewMode}
                  style={{ padding: '8px 20px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                >
                  ← Back
                </button>
                <button
                  onClick={handleSaveParameters}
                  disabled={analyzing || isViewMode}
                  style={{
                    padding: '10px 24px',
                    background: analyzing || isViewMode ? '#95a5a6' : '#e74c3c',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: analyzing || isViewMode ? 'not-allowed' : 'pointer',
                    fontSize: '13px'
                  }}
                >
                  {analyzing ? '🔍 Running...' : '🚀 Run Z-Score'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Results */}
      {step === 2 && results && (
        <div>
          {/* Summary Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, marginBottom: 20 }}>
            {[
              { label: 'Outliers', value: results.outlier_count?.toLocaleString(), color: '#e74c3c' },
              { label: 'Outlier %', value: `${results.outlier_pct}%`, color: '#f39c12' }
            ].map((card, i) => (
              <Paper key={i} sx={{ textAlign: 'center', padding: '16px', borderLeft: `4px solid ${card.color}`, borderRadius: '8px' }}>
                <Typography variant="h4" fontWeight={700} sx={{ color: card.color }}>{card.value}</Typography>
                <Typography variant="caption" color="textSecondary">{card.label}</Typography>
              </Paper>
            ))}
          </div>

          {/* Z-Score Plot */}
          <div style={gs}>
            <div style={{ ...ghs, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>📈 Z-Score Plot</span>
              <button 
                onClick={() => setShowScatterEditor(!showScatterEditor)}
                disabled={isViewMode}
                style={{ padding: '2px 8px', fontSize: 11, background: '#f0f0f0', border: '1px solid #ddd', borderRadius: 4, cursor: isViewMode ? 'not-allowed' : 'pointer' }}>
                {showScatterEditor ? '✕ Close' : '✏️ Edit'}
              </button>
            </div>
            <div style={gcs}>
              {showScatterEditor && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, padding: 12, background: '#f9f9f9', borderRadius: 6, marginBottom: 12 }}>
                  <div>
                    <label style={{ fontSize: 11 }}>Title</label>
                    <input type="text" value={scatterTitle} onChange={e => setScatterTitle(e.target.value)} style={{ width: '100%', padding: 6, fontSize: 12, border: '1px solid #ddd', borderRadius: 4 }} />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <button onClick={() => { renderScatter({ title: scatterTitle }); setShowScatterEditor(false); }}
                      style={{ padding: '6px 16px', background: '#1abc9c', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}>Apply</button>
                  </div>
                </div>
              )}
              <div ref={scatterRef} style={{ width: '100%', minHeight: 400 }}></div>
            </div>
          </div>

          {/* Boxplot */}
          <div style={gs}>
            <div style={{ ...ghs, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>📦 Boxplot</span>
              <button 
                onClick={() => setShowBoxplotEditor(!showBoxplotEditor)}
                disabled={isViewMode}
                style={{ padding: '2px 8px', fontSize: 11, background: '#f0f0f0', border: '1px solid #ddd', borderRadius: 4, cursor: isViewMode ? 'not-allowed' : 'pointer' }}>
                {showBoxplotEditor ? '✕ Close' : '✏️ Edit'}
              </button>
            </div>
            <div style={gcs}>
              {showBoxplotEditor && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, padding: 12, background: '#f9f9f9', borderRadius: 6, marginBottom: 12 }}>
                  <div>
                    <label style={{ fontSize: 11 }}>Title</label>
                    <input type="text" value={boxplotTitle} onChange={e => setBoxplotTitle(e.target.value)} style={{ width: '100%', padding: 6, fontSize: 12, border: '1px solid #ddd', borderRadius: 4 }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 11 }}>Y-Axis</label>
                    <input type="text" value={boxplotYLabel} onChange={e => setBoxplotYLabel(e.target.value)} style={{ width: '100%', padding: 6, fontSize: 12, border: '1px solid #ddd', borderRadius: 4 }} />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <button onClick={() => { renderBoxplot({ title: boxplotTitle, ylabel: boxplotYLabel }); setShowBoxplotEditor(false); }}
                      style={{ padding: '6px 16px', background: '#1abc9c', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}>Apply</button>
                  </div>
                </div>
              )}
              <div ref={boxplotRef} style={{ width: '100%', minHeight: 400 }}></div>
            </div>
          </div>

          {/* Histogram with Target Mean */}
          <div style={gs}>
            <div style={{ ...ghs, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>📊 Histogram + Target</span>
              <button 
                onClick={() => setShowHistEditor(!showHistEditor)}
                disabled={isViewMode}
                style={{ padding: '2px 8px', fontSize: 11, background: '#f0f0f0', border: '1px solid #ddd', borderRadius: 4, cursor: isViewMode ? 'not-allowed' : 'pointer' }}>
                {showHistEditor ? '✕ Close' : '✏️ Edit'}
              </button>
            </div>
            <div style={gcs}>
              {showHistEditor && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, padding: 12, background: '#f9f9f9', borderRadius: 6, marginBottom: 12 }}>
                  <div>
                    <label style={{ fontSize: 11 }}>Title</label>
                    <input type="text" value={histTitle} onChange={e => setHistTitle(e.target.value)} style={{ width: '100%', padding: 6, fontSize: 12, border: '1px solid #ddd', borderRadius: 4 }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 11 }}>X-Axis</label>
                    <input type="text" value={histXLabel} onChange={e => setHistXLabel(e.target.value)} style={{ width: '100%', padding: 6, fontSize: 12, border: '1px solid #ddd', borderRadius: 4 }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 11 }}>Y-Axis (Mean Target)</label>
                    <input type="text" value={histY2Label} onChange={e => setHistY2Label(e.target.value)} style={{ width: '100%', padding: 6, fontSize: 12, border: '1px solid #ddd', borderRadius: 4 }} />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <button onClick={() => { renderHist({ title: histTitle, xlabel: histXLabel, y2label: histY2Label }); setShowHistEditor(false); }}
                      style={{ padding: '6px 16px', background: '#1abc9c', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}>Apply</button>
                  </div>
                </div>
              )}
              <div ref={histRef} style={{ width: '100%', minHeight: 400 }}></div>
            </div>
          </div>

          {/* Comments Section - ALWAYS EDITABLE */}
          <div style={{ ...gs, marginTop: '20px' }}>
            <div 
              style={{ 
                ...ghs, 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                cursor: 'pointer',
                userSelect: 'none'
              }}
              onClick={() => setIsCommentsExpanded(!isCommentsExpanded)}
            >
              <span>💬 Comments & Discussions ({comments.length})</span>
              <span style={{ fontSize: '20px', fontWeight: 'bold' }}>
                {isCommentsExpanded ? '−' : '+'}
              </span>
            </div>
            
            {isCommentsExpanded && (
              <div style={gcs}>
                <div style={{ maxHeight: '300px', overflow: 'auto', marginBottom: '16px' }}>
                  {comments.length === 0 ? (
                    <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                      No comments yet. Be the first to add a comment.
                    </Typography>
                  ) : (
                    comments.map((comment) => (
                      <div
                        key={comment.id}
                        style={{
                          padding: '10px 12px',
                          marginBottom: '8px',
                          background: '#f8f9fa',
                          borderRadius: '8px',
                          borderLeft: '3px solid #e74c3c'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                          <span style={{ fontSize: '11px', fontWeight: 600, color: '#2c3e50' }}>
                            {comment.user_email || comment.user_id || 'Unknown'}
                          </span>
                          <span style={{ fontSize: '10px', color: '#95a5a6' }}>
                            {new Date(comment.created_at).toLocaleString()}
                          </span>
                        </div>
                        <div style={{ fontSize: '13px', color: '#34495e', whiteSpace: 'pre-wrap' }}>
                          {comment.text}
                        </div>
                      </div>
                    ))
                  )}
                </div>
                
                {/* Comment input - ALWAYS VISIBLE (before and after save) */}
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment or question..."
                    style={{
                      flex: 1,
                      padding: '10px',
                      fontSize: '13px',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      resize: 'vertical',
                      minHeight: '60px',
                      fontFamily: 'inherit'
                    }}
                  />
                  <button
                    onClick={handleAddComment}
                    disabled={savingComment || !newComment.trim()}
                    style={{
                      padding: '8px 20px',
                      background: (savingComment || !newComment.trim()) ? '#95a5a6' : '#e74c3c',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: (savingComment || !newComment.trim()) ? 'not-allowed' : 'pointer',
                      fontSize: '13px',
                      height: 'fit-content'
                    }}
                  >
                    {savingComment ? 'Sending...' : 'Add Comment'}
                  </button>
                </div>
                
                {/* Hint for unsaved comments */}
                {!savedAnalysisId && comments.length > 0 && (
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                    💡 Comments will be permanently saved when you click "Save Analysis"
                  </Typography>
                )}
              </div>
            )}
          </div>

          {/* Professional Action Area */}
          <div style={{ marginTop: '24px', padding: '20px', background: '#f8f9fa', borderRadius: '12px', border: '1px solid #e0e0e0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 500, display: 'block', marginBottom: 4 }}>Column Name</label>
                  <input 
                    type="text" 
                    value={saveColName} 
                    onChange={e => setSaveColName(e.target.value)}
                    placeholder={`${formData.Risk_driver?.value || 'var'}_zscore_outlier`}
                    style={{ padding: '8px 12px', fontSize: 12, border: '1px solid #ddd', borderRadius: 6, width: 240 }}
                    disabled={isViewMode}
                  />
                </div>
                <button 
                  onClick={handleSaveColumn} 
                  disabled={loading || isViewMode}
                  style={{ 
                    padding: '8px 20px', 
                    background: loading || isViewMode ? '#95a5a6' : '#27ae60', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: 6, 
                    cursor: loading || isViewMode ? 'not-allowed' : 'pointer', 
                    fontSize: 13, 
                    whiteSpace: 'nowrap',
                    marginBottom: '0px'
                  }}>
                  {loading ? 'Saving...' : '💾 Save Outlier Flags'}
                </button>
              </div>
              
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <MuiSelect
                  value={analysisStatus}
                  onChange={(e) => setAnalysisStatus(e.target.value)}
                  label="Status"
                  disabled={isViewMode}
                >
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="validated">Validated</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                </MuiSelect>
              </FormControl>
            </div>
            
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={analysisDescription}
              onChange={(e) => setAnalysisDescription(e.target.value)}
              placeholder="Describe the purpose, methodology, or key findings of this Z-Score analysis..."
              disabled={isViewMode}
              sx={{ mb: 3 }}
            />
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                onClick={() => setStep(1)}
                disabled={isViewMode}
                size="small"
              >
                ← Back
              </Button>
              
              <Button
                variant="outlined"
                onClick={() => { 
                  setStep(0); 
                  setSelectedDataset(null); 
                  setSelectedDatasetId('');
                  setColumns([]);
                  setResults(null); 
                  setAnalysisStatus('draft'); 
                  setAnalysisDescription(''); 
                  setSavedAnalysisId(null); 
                  setComments([]);
                  setFormData({
                    Default_Date: null, Risk_driver: null, Target_variable: null, n_bins: 10, threshold: 3.0
                  });
                  setScatterTitle('Z-Score Plot');
                  setBoxplotTitle('Boxplot');
                  setBoxplotYLabel('');
                  setHistTitle('Histogram with Target Mean');
                  setHistXLabel('');
                  setHistY2Label('Mean Target');
                  setSaveColName('');
                  setShowAdvancedOptions(false);
                }}
                disabled={isViewMode}
                size="small"
              >
                New Analysis
              </Button>
              
              {isEditMode && savedAnalysisId && (
                <Button
                  variant="outlined"
                  onClick={handleUpdateMetadata}
                  disabled={updatingMetadata}
                  size="small"
                >
                  {updatingMetadata ? 'Updating...' : 'Update Metadata'}
                </Button>
              )}
              
              <Button
                variant="contained"
                onClick={handleExport}
                size="small"
                sx={{ bgcolor: '#27ae60', '&:hover': { bgcolor: '#219a52' } }}
              >
                📥 Export
              </Button>
              
              {isEditMode && (
                <Button
                  variant="contained"
                  onClick={handleSaveAnalysis}
                  disabled={!results || loading}
                  size="small"
                  sx={{ bgcolor: results && !loading ? '#e74c3c' : '#95a5a6', '&:hover': { bgcolor: '#c0392b' } }}
                >
                  {loading ? 'Saving...' : '💾 Save Analysis'}
                </Button>
              )}
            </Box>
          </div>
        </div>
      )}
    </div>
  );
};

export default ZScore;